import type React from 'react'
import { useNavigate } from 'react-router-dom'
import { useOpenTariffs } from '../hooks/useOpenTariffs'
import { isPlainLeftClick, tariffsSearch, targetIsLive, type PublicTarget } from '../utils/linkTarget'
import { reachGoal } from '../utils/ym'

interface CardLinkProps {
	target?: PublicTarget | null
	className?: string
	children: React.ReactNode
	/** Цель Метрики на клик: имя и параметры (блок, карточка). Без неё клик не считается. */
	goal?: { name: string; params?: Record<string, unknown> }
	'aria-label'?: string
	style?: React.CSSProperties
	onMouseEnter?: React.MouseEventHandler
	onMouseLeave?: React.MouseEventHandler
}

/** Единая ссылка конструктора страниц: карточка, строка внутри карточки, кнопка блока.
 *
 *  Всегда настоящий <a href>: поисковик видит переход, средний клик и «копировать адрес» работают.
 *  Обычный клик по внутреннему адресу — переход без перезагрузки; тарифы открываются окном поверх
 *  страницы, как везде на сайте; внешний адрес — новая вкладка. Цели нет или она закрыта — рисуется
 *  обычный блок без перехода: вёрстка та же, клика нет. */
export default function CardLink({ target, className, children, goal, style, onMouseEnter, onMouseLeave, ...rest }: CardLinkProps) {
	const navigate = useNavigate()
	const openTariffs = useOpenTariffs()

	if (!targetIsLive(target)) {
		return (
			<div className={className} style={style} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} aria-label={rest['aria-label']}>
				{children}
			</div>
		)
	}

	const href = target.href
	const onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
		if (goal) reachGoal(goal.name, goal.params)
		if (target.external || !isPlainLeftClick(e)) return
		e.preventDefault()
		const tariffs = tariffsSearch(href)
		if (tariffs !== null) openTariffs(tariffs)
		else navigate(href)
	}

	return (
		<a
			href={href}
			className={className}
			style={style}
			onClick={onClick}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			aria-label={rest['aria-label']}
			{...(target.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
		>
			{children}
		</a>
	)
}
